import { Box, Button, DialogActions, DialogTitle, Divider, Drawer } from "@mui/material";
import { useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { intersect } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../api/adminApi";
import type { ObjectAccessCategory, ObjectClassDefinition } from "../../../api/types/objectTypes";
import type { AccessValue } from "../../common/AccessEditor";
import AccessEditor from "../../common/AccessEditor";
import type { UserAccess, UserAccessMatrixRow } from "./userAccessMatrixModel";
import {
  getCategoryOptions,
  getInitialAccessValueForBulkEditing,
  mapObjectInternalUserPermissionsToUserAccess,
  mapUserAccessToObjectInternalUserPermissionsRequest,
} from "./userAccessMatrixModel";

interface Props {
  objectId: string;
  objectDefinition: ObjectClassDefinition;
  categories: ObjectAccessCategory[];
  editedRows: UserAccessMatrixRow[];
  currentAccessItems: UserAccess[];
  onSave: (updatedAccessItems: UserAccess[]) => void;
  onClose: () => void;
}

const updateObjectInternalUserPermissions = withErrorHandling(adminApi.updateObjectInternalUserPermissions);

const EditInternalUserAccessDrawer = ({
  objectId,
  objectDefinition,
  categories,
  editedRows,
  currentAccessItems,
  onSave,
  onClose,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [accessValue, setAccessValue] = useState<AccessValue>(() =>
    getInitialAccessValueForBulkEditing(editedRows, { setRole: false })
  );

  const categoryOptions = useMemo(() => getCategoryOptions(categories), [categories]);

  const handleSave = async () => {
    const updatedItems: UserAccess[] = editedRows.map((c) => ({
      id: c.id,
      role: accessValue.selectedRole === "None" ? undefined : accessValue.selectedRole,
      categories: [
        ...accessValue.selectedCategoryIds,
        ...intersect(accessValue.indeterminateCategoryIds ?? [], c.categories),
      ],
    }));

    const otherItems = currentAccessItems.filter((item) => !editedRows.some((row) => row.id === item.id));

    const payload = {
      userPermissions: [...otherItems, ...updatedItems].map(mapUserAccessToObjectInternalUserPermissionsRequest),
    };

    setIsSaving(true);
    const [updatedUsers, error] = await updateObjectInternalUserPermissions(
      objectDefinition.objectType,
      objectId,
      payload
    );
    setIsSaving(false);

    if (error) {
      logError(error, "[EditInternalUserAccessDrawer] updateObjectInternalUserPermissions");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("User(s) updated successfully");
    onSave(updatedUsers.map(mapObjectInternalUserPermissionsToUserAccess));
    onClose();
  };

  const handleAccessChange = (update: Partial<AccessValue>) => {
    setAccessValue((prev) => ({ ...prev, ...update }));
    setIsDirty(true);
  };

  return (
    <Drawer anchor="right" open sx={{ "& .MuiDrawer-paper": { width: "25rem" } }}>
      <DialogTitle>
        {editedRows.length === 1 ? `Edit ${editedRows[0]?.name}` : `Bulk Edit ${editedRows.length} users`}
      </DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          py: 2.5,
          px: 3,
          height: "100%",
          overflow: "scroll",
        }}
      >
        <AccessEditor
          value={accessValue}
          onChange={handleAccessChange}
          roleOptions={[]}
          categoryOptions={categoryOptions}
        />
      </Box>
      <Divider />
      <DialogActions sx={{ py: 2, px: 3, columnGap: 1, justifyContent: "flex-start" }}>
        <Button variant="contained" loading={isSaving} onClick={handleSave} disabled={!isDirty}>
          Apply
        </Button>
        <Button variant="text" color="secondary" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Drawer>
  );
};

export default EditInternalUserAccessDrawer;
